import { EntityPipelineStageId, PipelineStageId, RegionId } from "@sp-crm/core";
import { useQueryClient } from "@tanstack/react-query";
import { trpc, trpcClient } from "components/rpc/trpc";
import { fancyAlert } from "components/ui/fancy-alert";
import { produce } from "immer";
import React from "react";
import { handleEvent } from "util/user-events";
import { ReferralPipelineColumn } from "./referral-pipeline-column";
import { BoardStageCard, PipelineSummary, ReferralPipelineBoardQuery } from "./types";

interface ReferralPipelineBoardProps {
    pipeline: PipelineSummary;
    regionId: RegionId;
    boardQuery: ReferralPipelineBoardQuery;
}

export const ReferralPipelineBoard: React.FC<ReferralPipelineBoardProps> = props => {
    const { pipeline, regionId, boardQuery } = props;
    const trpcContext = trpc.useTRPC();
    const queryClient = useQueryClient();
    const [highlightedCards, setHighlightedCards] = React.useState<string[]>([]);
    const [pendingCards, setPendingCards] = React.useState<string[]>([]);

    const moveCard = React.useCallback(
        async (
            membershipId: EntityPipelineStageId,
            fromStageId: PipelineStageId,
            toStageId: PipelineStageId,
        ) => {
            handleEvent("referral-pipeline-move-card", { source: "pipeline" });
            const fromKey = trpcContext.pipeline.boardStage.queryKey({
                stageId: fromStageId,
                regionId,
                ...boardQuery,
            });
            const toKey = trpcContext.pipeline.boardStage.queryKey({
                stageId: toStageId,
                regionId,
                ...boardQuery,
            });
            const fromData: BoardStageCard[] | undefined =
                queryClient.getQueryData(fromKey);
            const movedCard = fromData?.find(card => card.id === membershipId);
            if (movedCard) {
                queryClient.setQueryData(
                    fromKey,
                    (oldData: BoardStageCard[] | undefined) =>
                        oldData
                            ? produce(oldData, draft => {
                                  const index = draft.findIndex(
                                      card => card.id === membershipId,
                                  );
                                  if (index !== -1) {
                                      draft.splice(index, 1);
                                  }
                              })
                            : oldData,
                );
                queryClient.setQueryData(
                    toKey,
                    (oldData: BoardStageCard[] | undefined) =>
                        oldData
                            ? produce(oldData, draft => {
                                  draft.unshift(movedCard);
                              })
                            : oldData,
                );
            }
            try {
                await trpcClient.pipeline.moveCard.mutate({
                    membershipId,
                    stageId: toStageId,
                });
            } catch (error) {
                await fancyAlert(
                    "Unable to move",
                    error instanceof Error
                        ? error.message
                        : "Unable to move this referral source",
                );
            } finally {
                await Promise.all([
                    queryClient.refetchQueries({ queryKey: fromKey }),
                    queryClient.refetchQueries({ queryKey: toKey }),
                ]);
            }
        },
        [trpcContext, queryClient, regionId, boardQuery],
    );

    const dragHandler = React.useCallback(
        async (
            membershipId: EntityPipelineStageId,
            fromStageId: PipelineStageId,
            toStageId: PipelineStageId,
        ) => {
            if (fromStageId === toStageId) {
                return;
            }
            setHighlightedCards(list => [...list, membershipId]);
            setPendingCards(list => [...list, membershipId]);
            try {
                await moveCard(membershipId, fromStageId, toStageId);
            } finally {
                setPendingCards(list => list.filter(id => id !== membershipId));
                setTimeout(
                    () =>
                        setHighlightedCards(list =>
                            list.filter(id => id !== membershipId),
                        ),
                    1000,
                );
            }
        },
        [moveCard, setHighlightedCards, setPendingCards],
    );

    const removeCard = React.useCallback(
        async (stageId: PipelineStageId, card: BoardStageCard) => {
            handleEvent("referral-pipeline-remove-card", { source: "pipeline" });
            const stageKey = trpcContext.pipeline.boardStage.queryKey({
                stageId,
                regionId,
                ...boardQuery,
            });
            queryClient.setQueryData(stageKey, (oldData: BoardStageCard[] | undefined) =>
                oldData ? oldData.filter(c => c.id !== card.id) : oldData,
            );
            try {
                await trpcClient.pipeline.removeCard.mutate({ membershipId: card.id });
            } catch (error) {
                await fancyAlert(
                    "Unable to remove",
                    error instanceof Error
                        ? error.message
                        : "Unable to remove this referral source",
                );
            } finally {
                await queryClient.refetchQueries({ queryKey: stageKey });
            }
        },
        [trpcContext, queryClient, regionId, boardQuery],
    );

    return (
        <div className="pipeline row main-body">
            {pipeline.stages.map(stage => (
                <ReferralPipelineColumn
                    key={stage.id}
                    stage={stage}
                    regionId={regionId}
                    boardQuery={boardQuery}
                    dragHandler={dragHandler}
                    highlightedCards={highlightedCards}
                    pendingCards={pendingCards}
                    onRemove={removeCard}
                />
            ))}
        </div>
    );
};
